<!DOCTYPE HTML>
<template>
<div class="question"
     v-if="question">
  <div class="text">
    <h2>{{question.text}}</h2>
    <span class="instructions"
          v-if="question.multipleAnswers">
      Check all that apply
    </span>
  </div>

  <div class="options">
    <div v-if="question.type === 'slider'"
         class="slider">
      <div v-html="formattedRangeLabel"></div>


      <vue-slider class="custom-slider"
                  v-if="question.range.type == 'single'"
                  v-model="answers[questionId]"
                  :min="question.range.min"
                  :max="question.range.max"
                  :dotSize="24"
                  :drag-on-click="true"
                  :height="8"
                  tooltipPlacement="bottom" />

      <vue-slider class="custom-slider"
                  v-else
                  v-model="answer[0]"
                  :min="question.range.min"
                  :max="question.range.max"
                  :dotSize="24"
                  :drag-on-click="true"
                  :enable-cross="true"
                  :height="8"
                  tooltipPlacement="bottom"
                  :interval="question.range.interval ? question.range.interval : 1" />
    </div>

    <div v-else-if="question.type === 'input'"
         class="input">
      <div v-for="(option, index) in question.options"
           :key="option.id">
        {{option.text}}
        <b-input @change="updateAnswer($event, option, index )"
                 :value="option.default? option.default : null"
                 :type="option.type"></b-input>
      </div>
    </div>

    <div v-else>
      <div class="multiple"
           v-if="question.multipleAnswers">
        <b-button @click="selectOption(question , option)"
                  pill
                  size="sm"
                  :variant="isSelected(question, option)"
                  v-for="option in options"
                  :key="option.id">{{option.text}}</b-button>
      </div>
      <b-button-group v-else
                      class="group">
        <b-button @click="selectOption(question , option)"
                  size="sm"
                  :variant="isSelected(question, option)"
                  v-for="option in options"
                  :key="option.id">{{option.text? option.text : option}}</b-button>
      </b-button-group>
    </div>
  </div>

  <div v-if="question.extra"
       class="extra">
    <div>
      <h1>{{question.extra.headline}}</h1>
      <p>
        {{question.extra.body}}
      </p>
    </div>
    <template v-if="question.extra.id == 'average_rates'">
      <AverageRates :avg="averageRate" />
    </template>

    <template v-if="question.extra.id == 'budget_estimate'">
      <BudgetEstimate :budget="budget" />
    </template>
  </div>

  <div class="actions">

    <slot></slot>

    <b-button variant="outline-primary"
              v-show="previousButton.shown"
              @click="previousQuestion()">{{previousButton.label}}</b-button>
    <b-button variant="primary"
              @click="nextQuestion()"
              :disabled="nextButton.disabled">{{nextButton.label}}</b-button>
  </div>
</div>
</template>

<script>
import Vue from "vue";
const firebase = require( '@/firebaseConfig.js' )
import AverageRates from '@/components/AverageRates.vue'
import BudgetEstimate from '@/components/BudgetEstimate.vue'
import VueSlider from 'vue-slider-component'
export default {
  name: 'Question',
  components: {
    AverageRates,
    BudgetEstimate,
    VueSlider,
  },
  props: {
    answers: Object,
    questions: Array,
    questionCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      optionCollection: []
    }
  },
  watch: {
    /*
      Add a (reactive) placeholder for every answer, if there isn't one already.
    */
    questions: function() {
      this.questions.forEach( q => {
        if ( !this.answers[ q.id ] ) {
          let v = []
          if ( q.range ) {
            if ( q.range.type == 'single' ) {
              v = q.range.default
            } else if ( q.range.type == 'double' ) {
              v = [
                [ q.range.min, q.range.max ]
              ]
            }
          }
          if ( q.options ) {
            if ( q.options.find( o => o.default ) ) {
              v = q.options.map( o => o.default )
            }
          }
          Vue.set( this.answers, q.id, v )
        }
      } )
    },
    question() {
      let oc = this.question.optionCollection
      if ( oc ) {
        this.$bind( 'optionCollection', firebase.db.collection( oc ) )
      }
    }
  },
  computed: {
    questionId() {
      return this.questions[ this.questionCount ].id
    },
    question() {
      let q = this.questions.find( q => q.id == this.questionId )
      return q
    },
    answer() {
      return this.answers[ this.questionId ]
    },
    nextButton() {
      let isDisabled = this.answer.length == 0 ? true : false
      if ( this.question.skippable ) {
        isDisabled = false
      }
      return {
        'disabled': isDisabled,
        'label': this.questionCount == this.questions.length - 1 ? 'Finish' : 'Next'
      }
    },
    previousButton() {
      return {
        'shown': this.questionCount > 0 ? true : false,
        'label': 'Previous'
      }
    },
    formattedRangeLabel() {
      if ( this.question.range.label ) {
        if ( this.question.range.type == 'single' ) {
          let a = this.answer ? this.answer : this.question.range.min
          let result = this.question.range.label
          result = result.replace( `[0]`, a )
          result = result.replace( /\{(.+?)\}/g, "<span class='hilite'>$1</span>" );
          return result
        } else {
          let values = this.answer[ 0 ] ? this.answer[ 0 ] : [ this.question.range.min, this.question.range.max ]
          let result = this.question.range.label
          for ( const [ key, value ] of Object.entries( values ) ) {
            result = result.replace( `[${key}]`, value )
          }
          result = result.replace( /\{(.+?)\}/g, "<span class='hilite'>$1</span>" );
          return result
        }
      }
      return ''
    },
    averageRate() {
      let a = this.answer[ 0 ][ 0 ]
      let b = this.answer[ 0 ][ 1 ]
      let c = ( a + b ) / 2
      return Math.ceil( c / 10 ) * 10;
    },
    budget() {
      let a = parseInt( this.answer[ 0 ] )
      return a
    },
    options() {
      if ( this.question.optionCollection ) {
        return this.optionCollection
      } else {
        return this.question.options
      }
    }
  },
  methods: {
    selectOption( question, option ) {
      if ( question.multipleAnswers ) {
        let exists = this.answer.includes( option )
        if ( exists ) {
          let index = this.answer.indexOf( option )
          this.answer.splice( index, 1 )
        } else {
          this.answer.push( option )
        }
      } else {
        this.answer.splice( 0, 1, option )
      }
      this.$emit( 'started' )
    },
    isSelected( question, option ) {
      if ( this.answer ) {
        if ( question.multipleAnswers ) {
          return this.answer.includes( option ) ? 'success' : 'outline-success'
        }
        return this.answer[ 0 ] == option ? 'success' : 'outline-success'
      }

      return 'outline-success'
    },
    nextQuestion() {
      if ( this.questionCount < this.questions.length - 1 ) {
        this.updatePosition( this.questionCount + 1 )
      } else {
        this.finish()
      }
      this.$emit( 'started' )
    },
    finish() {
      this.$emit( 'finish' )
    },
    previousQuestion() {
      if ( this.questionCount > 0 ) {
        this.updatePosition( this.questionCount - 1 )
      }
    },
    updatePosition( newVal ) {
      this.$emit( 'update-position', newVal )
    },
    updateAnswer( newVal, option, id ) {
      this.answer.splice( id, 1, newVal )
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
@import "@/styles/global.scss";

.question {
    display: grid;
    grid-row-gap: 4rem;
    grid-template-rows: auto auto;
    padding: 1vw;

    .text {
        display: grid;
        align-content: center;

        .instructions {
            font-family: 'Roboto';
            font-style: italic;
        }
    }

    .options {
        max-width: 40rem;

        .slider {
            max-width: 30rem;
            display: grid;
            grid-row-gap: 1rem;

            div .hilite {
                color: theme-color-level('success',2);
                background: theme-color-level('success',-11);
                border-radius: 5rem;
                font-size: 1rem;
                padding: 0.5rem 1rem;
            }

        }

        .multiple .btn {
            margin-right: 0.5rem;
            margin-bottom: 0.5rem;
        }

        .group .btn {
            padding: 0.5rem 1rem;
        }

        .input {
            display: grid;
            grid-row-gap: 1rem;
        }
    }

    .extra {
        display: grid;
        grid-template-columns: 2fr 3fr;
        grid-auto-flow: column;
        grid-column-gap: 3rem;
        justify-content: space-between;
        align-items: center;
        padding: 2rem 4rem;
        color: theme-color-level('primary', 6);
        background: theme-color-level('primary', -12);

        > div {
            > h1 {
                font-size: 1rem;
                text-transform: uppercase;
                letter-spacing: 0.1rem;
            }

            > p {
                font-family: 'Roboto';
            }
        }
    }

    .actions {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: auto;
        justify-content: flex-end;
        column-gap: 0.5rem;
    }

}
</style>
